import {
  BIKE_TYPE,
  G,
  Gx,
  IoS,
  L,
  R,
  RUNNING_SURFACE,
  S,
  SURFACE_TYPE,
  kcal,
} from "src/components/Calculator/WattCalculatorFormula";
import {
  CalculatorBodyText,
  CalculatorHeadline,
  CalculatorListItem,
  CalculatorTextCenter,
  CalculatorTextHighlight,
  CalculatorTopicH2Headline,
  PreCalculatorHeadline,
} from "src/components/Calculator/CalculatorTypography";
import { MenuItem, makeStyles } from "@material-ui/core";
import React, { FC, useState } from "react";

import { CalculatorButton } from "src/components/Calculator/components/CalculatorButton";
import { CalculatorButtonLinkCentered } from "src/components/Calculator/components/CalculatorButtonLinkCentered";
import { CalculatorImage } from "src/components/Calculator/CalculatorImage";
import CalculatorInputSection from "src/components/Calculator/CalculatorInputSection";
import { CalculatorLayout } from "src/components/Calculator/CalculatorLayout";
import CalculatorMultipleSelect from "src/components/Calculator/CalculatorMultipleSelect";
import { CalculatorResultsTitle } from "src/components/Calculator/components/CalculatorResultsTitle";
import CalculatorValueContainer from "src/components/Calculator/CalculatorValueContainer";
import CycleverseDivider from "src/components/CycleverseDivider";
import { DisplayResult } from "src/components/Calculator/components/DisplayResult";
import { ErrorMessage } from "src/components/common/ErrorMessage";
import SeoHead from "src/components/Seo/SeoHead";
import { TheCycleverseTheme } from "src/theme";
import useTranslation from "next-translate/useTranslation";

const localNumber = (values, floatSize = 2, lang = "de-DE") => Number(values?.toFixed(floatSize))?.toLocaleString(lang);

const useStyles = makeStyles((theme: TheCycleverseTheme) => ({
  alignCenterContainer: {
    textAlign: "center",
  },
  alignCenter: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  bottomTitle: {
    fontFamily: "Roboto 300",
    fontSize: "16px",
    lineHeight: "23px",
    fontWeight: 900,
    marginBottom: "20px",
  },
  amount: {
    color: theme.amountColor,
  },
  amountOrange: {
    fontFamily: "Roboto 300",
    fontSize: "24px",
    fontWeight: "bold",
    color: theme.saleColor,
  },
  amountBlue: {
    fontFamily: "Roboto 300",
    fontSize: "24px",
    fontWeight: "bold",
    color: theme.buttonBackgroundColor,
  },
  image: {
    width: "70%",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  imageContainer: {
    textAlign: "center",
  },
}));

const BicycleWattCalculator: FC = () => {
  const { t, lang } = useTranslation("bicycleWattCalculator");
  const classes = useStyles();
  const [values, setValues] = useState({
    bodyWeight: 86,
    howTall: 178,
    bikeWeight: 8,
    luggageAccessoriesWeigh: 50,
    bikeType: BIKE_TYPE[0],
    runningSurface: RUNNING_SURFACE[0],
    trainingSessionDistance: 90,
    trainingDurationHour: null,
    trainingDurationMin: null,
    surfaceType: SURFACE_TYPE[0],
    trainingSystem: 12,
    journeyAverageHeight: 40,
    temperature: 20,
    weightRelatedPerformance: "0",
    overallPerformance: "0",
    calorieConsumption: "0",
    overcomingAirResistance: "0",
    overcomingRollingResistance: "0",
    inclinePerformance: "0",
    ioSValue: "0",
    hasError: false,
  });

  const handleInputOnChange = (name, value) => {
    if (name === "trainingDurationMin" || name === "trainingDurationHour") {
      setValues((el) => ({
        ...el,
        [name]: Number(value) === Number.NaN ? null : Number(value),
      }));
    } else {
      setValues((el) => ({
        ...el,
        [name]: value,
      }));
    }
  };

  const handleCalculate = () => {
    const trainingDurationHourValue = !isNaN(values?.trainingDurationHour) ? Number(values?.trainingDurationHour) : 0;
    const trainingDurationMinValue = !isNaN(values?.trainingDurationMin) ? Number(values?.trainingDurationMin) : 0;
    if (trainingDurationHourValue || trainingDurationMinValue) {
      const GValue = G(
        values?.howTall,
        values?.temperature,
        values?.trainingSystem,
        values?.runningSurface,
        values?.bodyWeight,
        values?.bikeWeight,
        values?.luggageAccessoriesWeigh,
        values?.surfaceType,
        trainingDurationHourValue,
        trainingDurationMinValue,
        values?.trainingSessionDistance,
        values?.bikeType,
        values?.journeyAverageHeight
      );

      const kcalValue = kcal(
        values?.howTall,
        values?.temperature,
        values?.trainingSystem,
        values?.runningSurface,
        values?.bodyWeight,
        values?.bikeWeight,
        values?.luggageAccessoriesWeigh,
        values?.surfaceType,
        trainingDurationHourValue,
        trainingDurationMinValue,
        values?.trainingSessionDistance,
        values?.bikeType,
        values?.journeyAverageHeight
      );

      const lValue = L(
        values?.howTall,
        values?.temperature,
        values?.trainingSystem,
        trainingDurationHourValue,
        trainingDurationMinValue,
        values?.trainingSessionDistance,
        values?.bikeType,
        values?.journeyAverageHeight
      );

      const rValue = R(
        values?.bodyWeight,
        values?.bikeWeight,
        values?.luggageAccessoriesWeigh,
        values?.runningSurface,
        values?.surfaceType,
        values?.trainingSessionDistance,
        trainingDurationHourValue,
        trainingDurationMinValue
      );
      const gxValue = Gx(
        values?.howTall,
        values?.temperature,
        values?.trainingSystem,
        values?.runningSurface,
        values?.bodyWeight,
        values?.bikeWeight,
        values?.luggageAccessoriesWeigh,
        values?.surfaceType,
        trainingDurationHourValue,
        trainingDurationMinValue,
        values?.trainingSessionDistance,
        values?.bikeType,
        values?.journeyAverageHeight
      );

      const sValues = S(
        values?.bodyWeight,
        values?.bikeWeight,
        values?.luggageAccessoriesWeigh,
        values?.trainingSystem,
        trainingDurationHourValue,
        trainingDurationMinValue
      );
      const ioSValue = IoS(values?.trainingSystem, values?.trainingSessionDistance);

      setValues((el) => ({
        ...el,
        overallPerformance: localNumber(Math.round(GValue), 0, lang) || "0",
        weightRelatedPerformance: localNumber(gxValue, 2, lang) || "0",
        overcomingAirResistance: localNumber(Math.round(lValue), 0, lang) || "0",
        overcomingRollingResistance: localNumber(Math.round(rValue), 0, lang) || "0",
        inclinePerformance: localNumber(Math.round(sValues), 0, lang) || "0",
        calorieConsumption: localNumber(Math.round(kcalValue), 0, lang) || "0",
        ioSValue: localNumber(ioSValue, 2, lang) || "0",
        hasError: false,
      }));
    } else {
      setValues((el) => ({
        ...el,
        overallPerformance: "0",
        weightRelatedPerformance: "0",
        overcomingAirResistance: "0",
        overcomingRollingResistance: "0",
        inclinePerformance: "0",
        calorieConsumption: "0",
        ioSValue: "0",
        hasError: true,
      }));
    }
  };
  return (
    <CalculatorLayout
      header={
        <div data-test="page-rechner-fahrrad-wattrechner">
          <PreCalculatorHeadline>{t("wattOpenGraphPreHeadline")}</PreCalculatorHeadline>
          <CalculatorHeadline>{t("wattOpenGraphHeadline")}</CalculatorHeadline>
        </div>
      }
    >
      <SeoHead
        title={t("wattCalculatorMetaTitel")}
        description={t("wattCalculatorMetaDescription")}
        ogImage="/images/calculators/The-Cycleverse-OpenGraph-Wattrechner.png"
        ogDescription={t("wattOpenGraphDescription")}
        ogTitle={t("wattOpenGraphTitel")}
      />
      <CalculatorImage
        src="/images/calculators/Fahrrad_Wattrechner_Header.svg"
        width={600}
        layout="intrinsic"
        alt={t("wattCalculatorText93")}
      />

      <CalculatorButton text={t("wattCalculatorButton1")} scroll idToScrollTo="calculator" />

      <CalculatorBodyText>{t("wattCalculatorText1")}</CalculatorBodyText>
      <CalculatorBodyText>{t("wattCalculatorText2")}</CalculatorBodyText>
      <div id="calculator" />
      <div className={classes.alignCenterContainer}>
        <CalculatorTopicH2Headline>{t("wattCalculatorText3")}</CalculatorTopicH2Headline>
      </div>
      <CalculatorBodyText>{t("wattCalculatorText4")}</CalculatorBodyText>
      <CalculatorBodyText>{t("wattCalculatorText5")}</CalculatorBodyText>
      <CalculatorInputSection
        inputTitle={t("wattCalculatorText6")}
        inputNumber="1"
        isSlider
        selectZr={values?.bodyWeight}
        selectMaxZrLabel={"130 kg"}
        selectMaxZr={130}
        selectMinZrLabel="50 kg"
        selectMinZr={50}
        selectZrLabelUnit={`${values?.bodyWeight} kg`}
        onChange={(e) => handleInputOnChange("bodyWeight", e)}
      />

      <CalculatorInputSection
        inputTitle={t("wattCalculatorText7")}
        inputNumber="2"
        isSlider
        selectZr={values?.howTall}
        selectMaxZrLabel={"205 cm"}
        selectMaxZr={205}
        selectMinZrLabel="150 cm"
        selectMinZr={150}
        selectZrLabelUnit={`${values?.howTall} cm`}
        onChange={(e) => handleInputOnChange("howTall", e)}
      />

      <CalculatorInputSection
        inputTitle={t("wattCalculatorText8")}
        inputNumber="3"
        isSlider
        selectZr={values?.bikeWeight}
        selectMaxZrLabel={"15 kg"}
        selectMaxZr={15}
        selectMinZrLabel="5 kg"
        selectMinZr={5}
        sliderStep={0.5}
        selectZrLabelUnit={`${values?.bikeWeight} kg`}
        onChange={(e) => handleInputOnChange("bikeWeight", e)}
      />

      <CalculatorInputSection
        inputTitle={t("wattCalculatorText9")}
        inputNumber="4"
        isSlider
        selectZr={values?.luggageAccessoriesWeigh}
        selectMaxZrLabel={"10000 g"}
        selectMaxZr={10000}
        selectMinZrLabel="0 g"
        selectMinZr={0}
        inputCaption={t("wattCalculatorText10")}
        selectZrLabelUnit={`${values?.luggageAccessoriesWeigh} g`}
        onChange={(e) => handleInputOnChange("luggageAccessoriesWeigh", e)}
      />

      <CalculatorMultipleSelect
        inputTitle={t("wattCalculatorText11")}
        inputNumber="5"
        inputProperties={[
          {
            value: values?.bikeType,
            onChange: (e) => handleInputOnChange("bikeType", e?.target?.value),
            children: BIKE_TYPE?.map((el) => (
              <MenuItem key={el} value={el}>
                {el}
              </MenuItem>
            )),
          },
          {
            value: values?.runningSurface,
            onChange: (e) => handleInputOnChange("runningSurface", e?.target?.value),
            children: RUNNING_SURFACE?.map((el) => (
              <MenuItem key={el} value={el}>
                {el}
              </MenuItem>
            )),
          },
          {
            value: values?.surfaceType,
            onChange: (e) => handleInputOnChange("surfaceType", e?.target?.value),
            children: SURFACE_TYPE?.map((el) => (
              <MenuItem key={el} value={el}>
                {el}
              </MenuItem>
            )),
          },
        ]}
      />

      <CalculatorInputSection
        inputTitle={t("wattCalculatorText12")}
        inputNumber="6"
        isSlider
        selectZr={values?.trainingSessionDistance}
        selectMaxZrLabel={"150 km"}
        selectMaxZr={150}
        selectMinZrLabel="1 km"
        selectMinZr={1}
        selectZrLabelUnit={`${values?.trainingSessionDistance} km`}
        onChange={(e) => handleInputOnChange("trainingSessionDistance", e)}
      />

      <CalculatorMultipleSelect
        inputTitle={t("wattCalculatorText13")}
        inputNumber="7"
        inputProperties={[
          {
            value: values.trainingDurationHour === 0 ? "0" : values?.trainingDurationHour || t("wattCalculatorText90"),
            onChange: (e) => {
              handleInputOnChange("trainingDurationHour", `${e?.target?.value}`);
            },
            children: [t("wattCalculatorText90"), 0, ...Array.from({ length: 24 }, (_, i) => i + 1)]?.map((el) => (
              <MenuItem key={el} value={el}>
                {el}
              </MenuItem>
            )),
          },
          {
            value: values.trainingDurationMin === 0 ? "0" : values?.trainingDurationMin || t("wattCalculatorText91"),
            onChange: (e) => handleInputOnChange("trainingDurationMin", `${e?.target?.value}`),
            children: [t("wattCalculatorText91"), 0, ...Array.from({ length: 60 }, (_, i) => i + 1)]?.map((el) => (
              <MenuItem key={el} value={el}>
                {el}
              </MenuItem>
            )),
          },
        ]}
      />

      <CalculatorInputSection
        inputTitle={t("wattCalculatorText14")}
        inputNumber="8"
        isSlider
        selectZr={values?.trainingSystem}
        selectMaxZrLabel={`40.000 m`}
        selectMaxZr={40000}
        selectMinZrLabel={`0 m`}
        selectMinZr={0}
        sliderStep={50}
        selectZrLabelUnit={`${values?.trainingSystem} ${t("wattCalculatorPercent11")}`}
        onChange={(e) => handleInputOnChange("trainingSystem", e)}
      />

      <CalculatorInputSection
        inputTitle={t("wattCalculatorText15")}
        inputNumber="9"
        isSlider
        selectZr={values?.journeyAverageHeight}
        selectMaxZrLabel={"3.000 m"}
        selectMaxZr={3000}
        selectMinZrLabel="0 m"
        selectMinZr={0}
        selectZrLabelUnit={`${values?.journeyAverageHeight} m`}
        inputCaption={t("wattCalculatorText16")}
        onChange={(e) => handleInputOnChange("journeyAverageHeight", e)}
      />
      <CalculatorInputSection
        inputTitle={t("wattCalculatorText17")}
        inputNumber="10"
        isSlider
        selectZr={values?.temperature}
        selectMaxZrLabel={`40 ${t("tempUnit")}`}
        selectMaxZr={40}
        selectMinZrLabel={`-10 ${t("tempUnit")}`}
        selectMinZr={-10}
        selectZrLabelUnit={`${values?.temperature} ${t("tempUnit")}`}
        onChange={(e) => handleInputOnChange("temperature", e)}
      />

      {values?.hasError ? <ErrorMessage message={t("wattCalculatorTextError")} /> : null}

      <CalculatorButton text={t("wattCalculatorButton2")} onClick={handleCalculate} />

      <div className={`${classes.alignCenter}`}>
        <CalculatorResultsTitle text={t("wattCalculatorText18")} />
        <DisplayResult
          text1={t("wattCalculatorText19")}
          result={`${values?.overallPerformance} Watt`}
          className={classes.amount}
        />
        <DisplayResult text1={t("wattCalculatorText20")} result={`${values?.weightRelatedPerformance} Watt/kg`} />
      </div>

      <CalculatorTextCenter>
        <div className={`${classes.bottomTitle}`}>
          <CalculatorBodyText>{t("wattCalculatorText21")}</CalculatorBodyText>
        </div>
      </CalculatorTextCenter>
      <div className={classes.alignCenter}>
        <CalculatorValueContainer
          data={[
            {
              key: t("wattCalculatorText22"),
              value: `${values?.overcomingAirResistance} Watt`,
            },
            {
              key: t("wattCalculatorText23"),
              value: `${values?.overcomingRollingResistance} Watt`,
            },
            {
              key: t("wattCalculatorText24"),
              value: `${values?.inclinePerformance} Watt`,
            },
            {
              key: t("wattCalculatorText92"),
              value: `${values?.ioSValue} %`,
            },
          ]}
        />
      </div>

      <div className={`${classes.alignCenter}`}>
        <CalculatorTopicH2Headline>
          {t("wattCalculatorText25")} <span className={classes.amountBlue}>{values?.calorieConsumption} kcal</span>{" "}
          {t("wattCalculatorText26")}
        </CalculatorTopicH2Headline>
      </div>
      <CalculatorBodyText>{t("wattCalculatorText27")}</CalculatorBodyText>

      <CalculatorListItem>{t("wattCalculatorText28")}</CalculatorListItem>
      <CalculatorListItem>{t("wattCalculatorText29")}</CalculatorListItem>
      <CalculatorListItem>{t("wattCalculatorText30")}</CalculatorListItem>
      <CalculatorListItem>{t("wattCalculatorText31")}</CalculatorListItem>

      <CalculatorBodyText>{t("wattCalculatorText32")}</CalculatorBodyText>
      <CalculatorTextCenter>
        <CalculatorTopicH2Headline>{t("wattCalculatorText33")}</CalculatorTopicH2Headline>
      </CalculatorTextCenter>

      <CalculatorButtonLinkCentered href="/products/search?s=Powermeter" text={t("wattCalculatorbutton2")} />

      <CycleverseDivider />

      <CalculatorTopicH2Headline>{t("wattCalculatorText34")}</CalculatorTopicH2Headline>
      <div className={classes.imageContainer}>
        <img
          src="/images/calculators/Faktoren_Wattleistung.svg"
          alt={t("wattCalculatorText94")}
          className={`${classes.image}`}
        />
      </div>
      <CalculatorBodyText>{t("wattCalculatorText35")}</CalculatorBodyText>
      <CalculatorBodyText>{t("wattCalculatorText36")}</CalculatorBodyText>

      <CalculatorListItem>{t("wattCalculatorText37")}</CalculatorListItem>
      <CalculatorListItem>{t("wattCalculatorText38")}</CalculatorListItem>
      <CalculatorListItem>{t("wattCalculatorText39")}</CalculatorListItem>

      <CalculatorTopicH2Headline>{t("wattCalculatorText40")}</CalculatorTopicH2Headline>
      <CalculatorBodyText>{t("wattCalculatorText41")}</CalculatorBodyText>
      <CalculatorTopicH2Headline>{t("wattCalculatorText42")}</CalculatorTopicH2Headline>
      <CalculatorBodyText>{t("wattCalculatorText43")}</CalculatorBodyText>
      <CalculatorTopicH2Headline>{t("wattCalculatorText44")}</CalculatorTopicH2Headline>
      <CalculatorBodyText>{t("wattCalculatorText45")}</CalculatorBodyText>
      <CalculatorTopicH2Headline>{t("wattCalculatorText46")}</CalculatorTopicH2Headline>
      <div className={classes.imageContainer}>
        <img
          src="/images/calculators/Kilo_VS_Watt_Icon.svg"
          alt={t("wattCalculatorText95")}
          className={`${classes.image}`}
        />
      </div>
      <CalculatorBodyText>{t("wattCalculatorText47")}</CalculatorBodyText>
      <div className={`${classes.bottomTitle}`}>
        <CalculatorBodyText>{t("wattCalculatorText50")} 1:</CalculatorBodyText>
      </div>
      <CalculatorBodyText>{t("wattCalculatorText51")}</CalculatorBodyText>
      <CalculatorBodyText>{t("wattCalculatorText52")}</CalculatorBodyText>
      <div className={`${classes.bottomTitle}`}>
        <CalculatorBodyText>{t("wattCalculatorText50")} 2:</CalculatorBodyText>
      </div>
      <CalculatorBodyText>{t("wattCalculatorText53")}</CalculatorBodyText>
      <CalculatorBodyText>{t("wattCalculatorText54")}</CalculatorBodyText>
      <CalculatorBodyText>{t("wattCalculatorText55")}</CalculatorBodyText>
      <CalculatorBodyText>{t("wattCalculatorText56")}</CalculatorBodyText>
      <div className={`${classes.bottomTitle}`}>
        <CalculatorBodyText>{t("wattCalculatorText57")}</CalculatorBodyText>
      </div>
      <CalculatorBodyText>{t("wattCalculatorText58")}</CalculatorBodyText>
      <CalculatorBodyText>{t("wattCalculatorText59")}</CalculatorBodyText>
      <div className={`${classes.bottomTitle}`}>
        <CalculatorBodyText>{t("wattCalculatorText60")}</CalculatorBodyText>
      </div>
      <CalculatorBodyText>{t("wattCalculatorText61")}</CalculatorBodyText>
      <CalculatorBodyText>{t("wattCalculatorText62")}</CalculatorBodyText>

      <CalculatorListItem>{t("wattCalculatorText63")}</CalculatorListItem>
      <CalculatorListItem>{t("wattCalculatorText64")}</CalculatorListItem>
      <CalculatorListItem>{t("wattCalculatorText65")}</CalculatorListItem>

      <CalculatorBodyText>{t("wattCalculatorText66")}</CalculatorBodyText>

      <CalculatorTopicH2Headline>{t("wattCalculatorText67")}</CalculatorTopicH2Headline>
      <CalculatorBodyText>{t("wattCalculatorText68")}</CalculatorBodyText>
      <CalculatorBodyText>{t("wattCalculatorText69")}</CalculatorBodyText>
      <CalculatorBodyText>
        {t("wattCalculatorText70")}
        <br />
        {t("wattCalculatorText71")}
        <br />
        {t("wattCalculatorText711")}
      </CalculatorBodyText>

      <div className={`${classes.bottomTitle}`}>
        <CalculatorBodyText>{t("wattCalculatorText72")} 1:</CalculatorBodyText>
      </div>
      <CalculatorBodyText>{t("wattCalculatorText73")}</CalculatorBodyText>
      <CalculatorTextHighlight>
        <div className={`${classes.bottomTitle}`}>
          <CalculatorBodyText>{t("wattCalculatorText74")}</CalculatorBodyText>
        </div>
      </CalculatorTextHighlight>
      <CalculatorTextHighlight>
        <div className={`${classes.bottomTitle}`}>
          <CalculatorBodyText>{t("wattCalculatorText75")}</CalculatorBodyText>
        </div>
      </CalculatorTextHighlight>

      <CalculatorBodyText />
      <div className={`${classes.bottomTitle}`}>
        <CalculatorBodyText>{t("wattCalculatorText72")} 2:</CalculatorBodyText>
      </div>
      <CalculatorBodyText>{t("wattCalculatorText76")}</CalculatorBodyText>
      <CalculatorBodyText>{t("wattCalculatorText77")}</CalculatorBodyText>
      <CalculatorTextHighlight>
        <div className={`${classes.bottomTitle}`}>
          <CalculatorBodyText>{t("wattCalculatorText78")}</CalculatorBodyText>
        </div>
      </CalculatorTextHighlight>
      <CalculatorTextHighlight>
        <div className={`${classes.bottomTitle}`}>
          <CalculatorBodyText>{t("wattCalculatorText79")}</CalculatorBodyText>
        </div>
      </CalculatorTextHighlight>

      <CalculatorBodyText />
      <div className={`${classes.bottomTitle}`}>
        <CalculatorBodyText>{t("wattCalculatorText72")} 3:</CalculatorBodyText>
      </div>
      <CalculatorBodyText>{t("wattCalculatorText80")}</CalculatorBodyText>
      <CalculatorBodyText>{t("wattCalculatorText81")}</CalculatorBodyText>
      <CalculatorTextHighlight>
        <div className={`${classes.bottomTitle}`}>
          <CalculatorBodyText>{t("wattCalculatorText82")}</CalculatorBodyText>
        </div>
      </CalculatorTextHighlight>
      <CalculatorTextHighlight>
        <div className={`${classes.bottomTitle}`}>
          <CalculatorBodyText>{t("wattCalculatorText83")}</CalculatorBodyText>
        </div>
      </CalculatorTextHighlight>

      <CalculatorTopicH2Headline>{t("wattCalculatorText84")}</CalculatorTopicH2Headline>
      <CalculatorBodyText>{t("wattCalculatorText85")}</CalculatorBodyText>
      <CalculatorBodyText>{t("wattCalculatorText86")}</CalculatorBodyText>
      <CalculatorBodyText>{t("wattCalculatorText87")}</CalculatorBodyText>

      <CalculatorButton text={t("wattCalculatorButton3")} scroll idToScrollTo="calculator" />
      <CalculatorTextCenter>
        <CalculatorTopicH2Headline>{t("wattCalculatorText89")}</CalculatorTopicH2Headline>
      </CalculatorTextCenter>

      <CalculatorButtonLinkCentered href="/products/search?s=Powermeter" text={t("wattCalculatorbutton2")} />
    </CalculatorLayout>
  );
};

export default BicycleWattCalculator;
